'use client';

import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, RotateCcw, Download } from 'lucide-react';

type Item = {
  id: string;
  name: string;
  userBusinessValue: number;
  timeCriticality: number;
  riskOpportunity: number;
  jobSize: number;
};

const FIB_OPTIONS = [1, 2, 3, 5, 8, 13, 20];

const STORAGE_KEY = 'planetroadmap.wsjf-calculator.v1';

function newItem(name = ''): Item {
  return {
    id: Math.random().toString(36).slice(2),
    name,
    userBusinessValue: 5,
    timeCriticality: 3,
    riskOpportunity: 3,
    jobSize: 5,
  };
}

function score(i: Item): number {
  if (!i.jobSize) return 0;
  return (i.userBusinessValue + i.timeCriticality + i.riskOpportunity) / i.jobSize;
}

export function WsjfCalculator() {
  const [items, setItems] = useState<Item[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Item[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          setItems(parsed);
          setHydrated(true);
          return;
        }
      }
    } catch {}
    setItems([
      { ...newItem('API rate limiting'), userBusinessValue: 8, timeCriticality: 13, riskOpportunity: 5, jobSize: 3 },
      { ...newItem('Dark mode'), userBusinessValue: 5, timeCriticality: 2, riskOpportunity: 1, jobSize: 5 },
      { ...newItem('SSO integration'), userBusinessValue: 13, timeCriticality: 8, riskOpportunity: 8, jobSize: 8 },
    ]);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      console.warn('Failed to persist to localStorage:', err);
    }
  }, [items, hydrated]);

  const sorted = useMemo(
    () => [...items].map((i) => ({ ...i, score: score(i) })).sort((a, b) => b.score - a.score),
    [items],
  );

  function update(id: string, patch: Partial<Item>) {
    setItems((curr) => curr.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }

  function remove(id: string) {
    setItems((curr) => curr.filter((i) => i.id !== id));
  }

  function add() {
    setItems((curr) => [...curr, newItem()]);
  }

  function reset() {
    if (!confirm('Clear all items and start over?')) return;
    setItems([newItem()]);
  }

  function exportCsv() {
    const rows = [
      ['Name', 'User-Business Value', 'Time Criticality', 'Risk/Opportunity', 'Job Size', 'WSJF Score'],
      ...sorted.map((i) => [
        i.name || 'Untitled',
        i.userBusinessValue,
        i.timeCriticality,
        i.riskOpportunity,
        i.jobSize,
        i.score.toFixed(2),
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wsjf-scores.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!hydrated) {
    return (
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-12 text-center text-muted-foreground">
        Loading calculator…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 bg-muted/30">
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground w-8">#</th>
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground min-w-[200px]">
                  Feature
                </th>
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground">
                  User-Business Value
                  <div className="text-[10px] font-normal text-muted-foreground/70">Fibonacci</div>
                </th>
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground">
                  Time Criticality
                  <div className="text-[10px] font-normal text-muted-foreground/70">Fibonacci</div>
                </th>
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground">
                  Risk / Opportunity
                  <div className="text-[10px] font-normal text-muted-foreground/70">Fibonacci</div>
                </th>
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground">
                  Job Size
                  <div className="text-[10px] font-normal text-muted-foreground/70">Fibonacci</div>
                </th>
                <th className="text-right py-3 px-4 font-semibold text-primary">WSJF</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((item, i) => (
                <tr
                  key={item.id}
                  className={i < sorted.length - 1 ? 'border-b border-border/40' : ''}
                >
                  <td className="py-2 px-4 text-muted-foreground font-mono text-xs">{i + 1}</td>
                  <td className="py-2 px-4">
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => update(item.id, { name: e.target.value })}
                      placeholder="Untitled feature"
                      className="w-full bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                    />
                  </td>
                  <td className="py-2 px-4">
                    <select
                      value={item.userBusinessValue}
                      onChange={(e) => update(item.id, { userBusinessValue: Number(e.target.value) })}
                      className="bg-transparent border border-border/60 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      {FIB_OPTIONS.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-4">
                    <select
                      value={item.timeCriticality}
                      onChange={(e) => update(item.id, { timeCriticality: Number(e.target.value) })}
                      className="bg-transparent border border-border/60 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      {FIB_OPTIONS.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-4">
                    <select
                      value={item.riskOpportunity}
                      onChange={(e) => update(item.id, { riskOpportunity: Number(e.target.value) })}
                      className="bg-transparent border border-border/60 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      {FIB_OPTIONS.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-4">
                    <select
                      value={item.jobSize}
                      onChange={(e) => update(item.id, { jobSize: Number(e.target.value) })}
                      className="bg-transparent border border-border/60 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      {FIB_OPTIONS.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-4 text-right font-mono font-semibold text-primary">
                    {item.score.toFixed(1)}
                  </td>
                  <td className="py-2 px-2">
                    <button
                      onClick={() => remove(item.id)}
                      aria-label="Remove item"
                      className="text-muted-foreground hover:text-red-500 p-1 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-muted-foreground">
                    No features yet. Add one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-between items-center">
        <button
          onClick={add}
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold shadow-sm hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 transition-all"
        >
          <Plus className="w-4 h-4" />
          Add feature
        </button>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            disabled={sorted.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 disabled:opacity-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center pt-2">
        Your scores are saved automatically in your browser. Nothing leaves this page.
      </p>
    </div>
  );
}
